import { signOut, useSession } from '@/lib/auth';
import { Arrow } from '@levamar/icons';
import { Link, useRouter } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';

const SECTIONS = [
  { to: '/dashboard', label: 'DASHBOARD' },
  { to: '/bookings', label: 'RESERVAS' },
  { to: '/fleet', label: 'FROTA' },
  { to: '/locations', label: 'LOCAIS' },
  { to: '/customers', label: 'CLIENTES' },
  { to: '/payments', label: 'PAGAMENTOS' },
  { to: '/reviews', label: 'AVALIAÇÕES' },
  { to: '/settings', label: 'DEFINIÇÕES' },
  { to: '/audit', label: 'AUDITORIA' },
] as const;

/**
 * Admin shell — v2 design language.
 * Dense black sidebar with numbered nav, brand-teal left-border accent on active item.
 * Topbar: kicker label above display-font page title + 48px h-rule.
 * Matches `designs/Levamar Rentacar/src/v2-admin.jsx :: V2AdminShell` layout.
 */
export function Shell({
  children,
  page,
  sub,
}: {
  children: ReactNode;
  page: string;
  sub?: string;
}) {
  const router = useRouter();
  const { data: session } = useSession();
  const activePath = router.state.location.pathname;
  // Mobile drawer (<900px, see main.css .admin-*). Closes on navigation.
  const [open, setOpen] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: close whenever the route changes
  useEffect(() => setOpen(false), [activePath]);

  const initials = (session?.user?.name ?? session?.user?.email ?? 'AD')
    .split(' ')
    .map((p: string) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="admin-shell"
      data-open={open || undefined}
      style={{ display: 'flex', minHeight: '100vh', background: 'var(--lv-surface-soft)' }}
    >
      {/* ── Mobile top bar (hidden ≥900px) ── */}
      <div className="admin-topbar">
        <button
          type="button"
          className="admin-burger"
          aria-label={open ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
        <span className="display" style={{ fontSize: 18, color: '#fff' }}>
          LEVAMAR
        </span>
      </div>
      <div className="admin-backdrop" onClick={() => setOpen(false)} aria-hidden />

      {/* ── Sidebar ── */}
      <aside
        className="admin-side"
        style={{
          width: 240,
          background: 'var(--lv-night)',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: '100vh',
          flexShrink: 0,
        }}
      >
        {/* Brand lockup */}
        <div
          style={{
            padding: '22px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div>
            <div className="display" style={{ fontSize: 20, color: '#fff' }}>
              LEVAMAR
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: '0.16em',
                color: 'var(--lv-brand-glow)',
                fontWeight: 700,
                marginTop: 2,
              }}
            >
              ADMIN PANEL
            </div>
          </div>
        </div>

        {/* Nav section */}
        <div style={{ padding: '16px 0', flex: 1, overflowY: 'auto' }}>
          <div
            className="label"
            style={{ color: 'rgba(255,255,255,0.35)', padding: '0 20px 10px', fontSize: 9 }}
          >
            GESTÃO
          </div>

          {SECTIONS.map((s, i) => {
            // Segment-boundary match so a future /fleet-x never lights up /fleet.
            const active = activePath === s.to || activePath.startsWith(`${s.to}/`);
            return (
              <Link
                key={s.to}
                to={s.to}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '11px 20px',
                  borderLeft: active ? '4px solid var(--lv-brand-glow)' : '4px solid transparent',
                  background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
                  color: active ? '#fff' : 'rgba(255,255,255,0.6)',
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  textDecoration: 'none',
                  letterSpacing: '0.04em',
                  transition: 'background 0.1s, color 0.1s',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--lv-font-display)',
                    fontSize: 13,
                    fontWeight: 900,
                    color: active ? 'var(--lv-brand-glow)' : 'rgba(255,255,255,0.3)',
                    width: 22,
                    flexShrink: 0,
                  }}
                >
                  0{i + 1}
                </span>
                {s.label}
              </Link>
            );
          })}
        </div>

        {/* User footer */}
        <div
          style={{
            padding: '14px 16px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div
              style={{
                width: 36,
                height: 36,
                background: 'var(--lv-brand)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {session?.user?.name ?? session?.user?.email ?? '—'}
              </div>
              <button
                type="button"
                onClick={() => signOut().then(() => router.navigate({ to: '/login' }))}
                style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.4)',
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  marginTop: 2,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                Terminar sessão
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Main content area ── */}
      <main className="admin-main" style={{ flex: 1, minWidth: 0 }}>
        {/* Topbar — kicker + display title */}
        <header
          className="admin-header"
          style={{
            background: '#fff',
            borderBottom: '1px solid var(--lv-line)',
            padding: '18px 28px 16px',
          }}
        >
          {sub && (
            <div className="label" style={{ color: 'var(--lv-gray-500)', marginBottom: 6 }}>
              {sub}
            </div>
          )}
          <div className="display" style={{ fontSize: 32, lineHeight: 1, color: 'var(--lv-ink)' }}>
            {page}
          </div>
          <div className="h-rule" style={{ marginTop: 10 }} />
        </header>

        <div className="admin-content" style={{ padding: 28 }}>
          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * KPI Card — matches the V2AdminDashboard 4-card row exactly.
 * White bg, single-pixel border, label-value-delta stack.
 */
export function StatCard({
  label,
  value,
  delta,
  color,
  deltaColor,
}: {
  label: string;
  value: string;
  delta?: string;
  color?: string;
  deltaColor?: string;
}) {
  return (
    <div
      style={{
        background: '#fff',
        padding: '22px 24px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="label" style={{ color: 'var(--lv-gray-500)' }}>
        {label}
      </div>
      <div
        className="display"
        style={{
          fontSize: 52,
          color: color ?? 'var(--lv-ink)',
          marginTop: 8,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      {delta && (
        <div
          style={{
            fontSize: 11,
            color: deltaColor ?? 'var(--lv-ok)',
            fontWeight: 700,
            marginTop: 6,
          }}
        >
          {delta}
        </div>
      )}
    </div>
  );
}

export function NavArrow() {
  return <Arrow size={14} color="currentColor" />;
}
